/**
 * Runtime visibility controls for in-development (WIP) cards and surfaces.
 *
 * These helpers let the team preview the product the way end-users will see it
 * by suppressing WIP content without rebuilding. Runtime overrides are ignored
 * in production builds so shipping surfaces never leak.
 *
 * Resolution order for WIP visibility:
 *  1. Build-time env:       `VITE_WIP_FEATURES=true|false|1|0`
 *  2. Dev localStorage key: `finance:wipFeatures`
 *  3. Fallback:             visible only in development mode
 */

const WIP_FEATURES_STORAGE_KEY = 'finance:wipFeatures';

function parseOverride(value: string | null | undefined): boolean | undefined {
  if (value === null || value === undefined) return undefined;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'true' || normalized === '1') return true;
  if (normalized === 'false' || normalized === '0') return false;
  return undefined;
}

function getEnvOverride(): string | undefined {
  return import.meta.env?.VITE_WIP_FEATURES;
}

function getStorageOverride(): string | null {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(WIP_FEATURES_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function isWipFeaturesVisible(): boolean {
  const env = parseOverride(getEnvOverride());
  if (typeof env === 'boolean') return env;

  if (!import.meta.env.DEV) return false;

  const storage = parseOverride(getStorageOverride());
  if (typeof storage === 'boolean') return storage;

  return true;
}

export function setWipFeaturesVisible(visible: boolean): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  localStorage.setItem(WIP_FEATURES_STORAGE_KEY, String(visible));
}
